<template>
  <div class="list-room-decoration-record-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('listRoomDecorationRecord.page.title')" :subtitle="subtitleText">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddModal(roomDecorationRecordsInfo.roomRenovation)">
          {{ $t('listRoomDecorationRecord.add') }}
        </el-button>
        <el-button icon="el-icon-back" @click="_goBack()">
          {{ $t('listRoomDecorationRecord.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="refreshList">
        <template #left>
          <span class="ds-panel__title">{{ $t('listRoomDecorationRecord.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="roomDecorationRecordsInfo.roomRenovationRecords" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('listRoomDecorationRecord.page.empty')"
        @retry="refreshList">
        <el-table-column prop="recordId" :label="$t('listRoomDecorationRecord.recordId')" align="center" min-width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.recordId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="roomName" :label="$t('listRoomDecorationRecord.room')" align="center" min-width="120" />
        <el-table-column prop="staffName" :label="$t('listRoomDecorationRecord.operator')" align="center" width="100" />
        <el-table-column prop="createTime" :label="$t('listRoomDecorationRecord.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('listRoomDecorationRecord.status')" align="center" width="140">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="resolveStateLabel(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column prop="isTrueName" :label="$t('listRoomDecorationRecord.isViolation')" align="center" width="100">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.isTrue === 'true' ? 'danger' : 'success'"
              :label="scope.row.isTrueName || '-'" />
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('listRoomDecorationRecord.remark')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('listRoomDecorationRecord.operation')" align="center" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="_openRoomRenovationRecordDetailsModel(scope.row)">
              {{ $t('listRoomDecorationRecord.viewDetails') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="_openDeleteRoomRenovationRecordModel(scope.row)">
              {{ $t('listRoomDecorationRecord.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('listRoomDecorationRecord.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <room-decoration-record ref="roomDecorationRecord" @success="handleSuccess" />
    <delete-room-decoration-record ref="deleteRoomDecorationRecord" @success="handleSuccess" />
  </div>
</template>

<script>
import { queryRoomRenovationRecord } from '@/api/community/listRoomDecorationRecordApi'
import { getCommunityId } from '@/api/community/communityApi'
import RoomDecorationRecord from '@/components/community/RoomDecorationRecord'
import DeleteRoomDecorationRecord from '@/components/community/DeleteRoomDecorationRecord'
import { formatDateTimeSec } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ListRoomDecorationRecord',
  components: {
    RoomDecorationRecord,
    DeleteRoomDecorationRecord,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      roomDecorationRecordsInfo: {
        roomRenovationRecords: [],
        roomRenovation: [],
        conditions: {
          rId: '',
          roomName: '',
          roomId: '',
          stateName: '',
          state: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  computed: {
    /**
     * 副标题：房屋编号 + 当前装修状态（来源于路由 query，不额外请求）
     */
    subtitleText() {
      const roomName = this.roomDecorationRecordsInfo.conditions.roomName
      const stateName = this.roomDecorationRecordsInfo.conditions.stateName
      const parts = []
      if (roomName) parts.push(roomName)
      if (stateName) parts.push(stateName)
      return parts.length ? parts.join(' · ') : this.$t('listRoomDecorationRecord.page.subtitle')
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.roomDecorationRecordsInfo.conditions.communityId = this.communityId
    this.roomDecorationRecordsInfo.conditions.rId = this.$route.query.rId
    this.roomDecorationRecordsInfo.conditions.roomId = this.$route.query.roomId
    this.roomDecorationRecordsInfo.conditions.roomName = this.$route.query.roomName
    this.roomDecorationRecordsInfo.conditions.state = this.$route.query.state
    this.roomDecorationRecordsInfo.conditions.stateName = this.$route.query.stateName
    this._listRoomRenovationRecords(this.page.current, this.page.size)
  },
  methods: {
    async _listRoomRenovationRecords(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page,
          row: size,
          ...this.roomDecorationRecordsInfo.conditions
        }
        const { data, total } = await queryRoomRenovationRecord(params)
        this.roomDecorationRecordsInfo.roomRenovationRecords = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取装修记录失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('listRoomDecorationRecord.page.loadFailed')
        this.$message.error(this.$t('listRoomDecorationRecord.fetchError'))
      } finally {
        this.loading = false
      }
    },
    /**
     * 刷新当前页（工具条刷新 / 错误态重试）
     */
    refreshList() {
      this._listRoomRenovationRecords(this.page.current, this.page.size)
    },
    _openAddModal(roomRenovation) {
      roomRenovation.push(this.roomDecorationRecordsInfo.conditions.rId)
      roomRenovation.push(this.roomDecorationRecordsInfo.conditions.roomId)
      roomRenovation.push(this.roomDecorationRecordsInfo.conditions.roomName)
      roomRenovation.push(this.roomDecorationRecordsInfo.conditions.state)
      roomRenovation.push(this.roomDecorationRecordsInfo.conditions.stateName)
      this.$refs.roomDecorationRecord.open(roomRenovation)
    },
    _openDeleteRoomRenovationRecordModel(record) {
      this.$refs.deleteRoomDecorationRecord.open(record)
    },
    _openRoomRenovationRecordDetailsModel(record) {
      this.$router.push({
        path: '/views/community/listRoomRenovationRecordDetails',
        query: {
          recordId: record.recordId,
          roomName: record.roomName,
          state: record.state
        }
      })
    },
    handleSuccess() {
      this._listRoomRenovationRecords(this.page.current, this.page.size)
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listRoomRenovationRecords(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listRoomRenovationRecords(val, this.page.size)
    },
    _goBack() {
      this.$router.go(-1)
    },
    /**
     * 创建时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    /**
     * 装修记录状态语义类型（room_renovation_record.state）：
     * 1000 待审核 / 2000 审核不通过 / 3000 装修中 / 4000 待验收 / 5000 验收成功 / 6000 验收失败
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'roomRenovationRecord',
        text: row.stateName || ''
      }).type
    },
    /**
     * 装修记录状态文案：1000（原始图片）需保留「原始图片」后缀语义
     */
    resolveStateLabel(row) {
      if (!row) return '-'
      const label = row.stateName || '-'
      if (row.state === '1000') {
        return label + '(' + this.$t('listRoomDecorationRecord.originalImage') + ')'
      }
      return label
    }
  }
}
</script>

<style lang="scss" scoped>
.list-room-decoration-record-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
